import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';

import {
    Button,
    IconQuestionCircle,
    IconX,
    LoadingSpinner,
} from '@trqk/components';
import { locale, updateURLSearchParams } from '@trqk/utils';

import { modalHeight, modalWidth, stepContentHeight } from '../constants';
import { PATHS, URL_PARAMS } from '../../../../constants';
import { Breadcrumbs, DataSources } from '.';

interface StepWrapperProps {
    /**
     * Optional callback invoked before the user goes to the next step. Must return a Promise that
     * resolves to a boolean. If the Promise resolves to `true`, the user proceeds to the next
     * step. Any errors should be handled within this callback, i.e. not thrown to its caller,
     * and if this callback returns false, it should show a toast that alerts the user that
     * something went wrong.
     */
    beforeNextStep?(): Promise<boolean>;
    /**
     * Optional callback invoked before the user goes to the previous step. Must return a Promise
     * that resolves to a boolean. If the Promise resolves to `true`, the user proceeds to the
     * previous step. Any errors should be handled within this callback, i.e. not thrown to its
     * caller, and if this callback returns false, it should show a toast that alerts the user that
     * something went wrong.
     */
    beforePreviousStep?(): Promise<boolean>;
    children: ReactNode;
    context: GapAnalysisContext;
    /**
     * Optional extra elements to display to the left of the button that leads the user to the
     * next step.
     */
    extraButtons?: ReactNode;
    /**
     * Optional JSX to render when `context.loading === true`. If not provided, a LoadingSpinner
     * is shown when `context.loading === true`.
     */
    loadingState?: Maybe<JSX.Element>;
    remove(removeModalInput: RemoveModalInput): void;
    /**
     * When true, the previous step.title will be set to `null` when going to the previous step -
     * this means the breadcrumbs will show the `step.defaultTitle` instead.
     * @defaultValue false
     */
    resetPreviousStepTitle?: boolean;
    send: SendGapAnalysisEvent;
    /**
     * When defined, this is used as the href for an <a> tag linking to the FAQ at the upper right.
     * If this prop is not defined, the link and its preceding text are not shown.
     */
    helpLinkHref?: string;
}

/**
 * Wrapper for a Create Gap Analysis step component.
 * @param props - {@link ModalContentProps}
 */
export const StepWrapper = ({
    beforeNextStep,
    beforePreviousStep,
    children,
    context,
    extraButtons,
    loadingState,
    remove,
    resetPreviousStepTitle = false,
    send,
    helpLinkHref,
}: StepWrapperProps): JSX.Element | null => {
    const { mode, stepHistory, steps, reportDescription, trqkGap } = context;
    const currentStepId = stepHistory?.[stepHistory.length - 1] ?? null;

    if (currentStepId === null) {
        return null;
    }

    const currentStep = steps[currentStepId];
    const { nextStepId, previousStepId } = currentStep;

    const description =
        reportDescription.length > 0
            ? reportDescription
            : trqkGap?.description ?? '';

    const showDescription =
        description.length > 0 &&
        [
            'selectOptions',
            'analyzingData',
            'confirmChannels',
            'confirmWorks',
        ].includes(currentStepId);

    const title =
        mode === 'create'
            ? locale.enUS.MANAGE_GAP_ANALYSIS.TITLE.CREATE
            : locale.enUS.MANAGE_GAP_ANALYSIS.TITLE.EDIT;

    return (
        <div
            className="rounded-lg bg-white flex flex-col justify-between"
            style={{ height: modalHeight, width: modalWidth }}
        >
            <div className="flex-none border-l-4 border-secondary-500 rounded-tl-lg pt-4 mr-4 pb-4">
                <div
                    className="grid pr-4"
                    style={{ gridTemplateColumns: 'auto 230px 24px' }}
                >
                    <h3 className="text-xl ml-10 mt-6 truncate">
                        {showDescription ? description : title}
                    </h3>
                    <div
                        className={cx('mr-2 self-center flex', {
                            invisible: helpLinkHref === undefined,
                        })}
                    >
                        <IconQuestionCircle
                            fillColor="tertiary-400"
                            width={20}
                        />
                        <p className="text-sm text-tertiary-400 italic pl-1">
                            {
                                locale.enUS.MANAGE_GAP_ANALYSIS.STEP_WRAPPER
                                    .HELP_INFO.NEED_HELP
                            }{' '}
                            <a
                                href={helpLinkHref}
                                className="text-primary-500"
                                target="_blank"
                                rel="noreferrer"
                            >
                                {
                                    locale.enUS.MANAGE_GAP_ANALYSIS.STEP_WRAPPER
                                        .HELP_INFO.FAQ
                                }
                            </a>
                            .
                        </p>
                    </div>
                    <Link
                        to={({ search }) => {
                            const nextSearch = updateURLSearchParams(search, [
                                {
                                    key: URL_PARAMS.GAP_ANALYSIS_ID,
                                    value: null,
                                },
                                {
                                    key: URL_PARAMS.GAP_ANALYSIS_MODE,
                                    value: null,
                                },
                            ]);

                            return {
                                pathname: PATHS.GAP_ANALYSIS,
                                search: nextSearch,
                                state: {
                                    noAnchorScroll: true,
                                },
                            };
                        }}
                    >
                        <button
                            className="mt-5 mr-5 respond-to-mouse"
                            type="button"
                            onClick={(): void => {
                                remove({
                                    canceled: true,
                                    success: false,
                                });
                            }}
                        >
                            <IconX width={20} fillColor="tertiary-400" />
                        </button>
                    </Link>
                </div>
                <div className="ml-10 mt-1">
                    <Breadcrumbs context={context} />
                    {currentStep.showDataSources && (
                        <DataSources context={context} />
                    )}
                </div>
            </div>
            {context.loading && (
                <div className="flex-grow flex flex-col justify-center items-center">
                    {!loadingState && <LoadingSpinner />}
                    {loadingState && <>{loadingState}</>}
                </div>
            )}
            <div
                className={cx('px-12 pb-12', {
                    hidden: context.loading,
                })}
            >
                <div
                    style={{ height: stepContentHeight }}
                    className="flex-grow flex flex-col justify-between overflow-y-auto"
                >
                    {children}
                    {currentStep.statusMessage && (
                        <div className="flex justify-end mt-4">
                            <div>{currentStep.statusMessage}</div>
                        </div>
                    )}
                </div>

                <div className="flex items-baseline justify-between mt-4">
                    {previousStepId !== null && (
                        <Button
                            addClasses="respond-to-mouse"
                            autoWidth={true}
                            color="tertiary-light-bg"
                            disabled={currentStep.disablePreviousStep}
                            handleClick={() => {
                                if (beforePreviousStep) {
                                    send({
                                        type: 'SET_LOADING',
                                        value: {
                                            loading: true,
                                        },
                                    });

                                    beforePreviousStep()
                                        .then((success: boolean) => {
                                            send({
                                                type: 'SET_LOADING',
                                                value: {
                                                    loading: false,
                                                },
                                            });

                                            if (success) {
                                                send({
                                                    type: 'GO_TO_PREVIOUS_STEP',
                                                    value: {
                                                        resetPreviousStepTitle,
                                                    },
                                                });
                                            }
                                        })
                                        .catch(() => {
                                            // do nothing; errors should be handled by the
                                            // beforePreviousStep callback
                                        });
                                } else {
                                    send({
                                        type: 'GO_TO_PREVIOUS_STEP',
                                        value: {
                                            resetPreviousStepTitle,
                                        },
                                    });
                                }
                            }}
                        >
                            <p className="px-4">
                                {
                                    locale.enUS.MANAGE_GAP_ANALYSIS
                                        .BACK_BUTTON_LABEL
                                }
                            </p>
                        </Button>
                    )}
                    {/*
                        When there isn't a step to go back to, this holds the place of the back
                        button to keep the layout consistent.
                    */}
                    {previousStepId === null && <div />}
                    <div className="space-x-8">
                        {extraButtons && <>{extraButtons}</>}
                        {currentStep.nextStepId !== null && (
                            <Button
                                addClasses="respond-to-mouse"
                                autoWidth={true}
                                color="primary-light-bg"
                                disabled={currentStep.disableNextStep}
                                handleClick={() => {
                                    if (nextStepId !== null) {
                                        if (beforeNextStep) {
                                            send({
                                                type: 'SET_LOADING',
                                                value: {
                                                    loading: true,
                                                },
                                            });

                                            beforeNextStep()
                                                .then((success: boolean) => {
                                                    send({
                                                        type: 'SET_LOADING',
                                                        value: {
                                                            loading: false,
                                                        },
                                                    });

                                                    if (success) {
                                                        send({
                                                            type: 'GO_TO_NEXT_STEP',
                                                            value: {
                                                                nextStepId,
                                                            },
                                                        });
                                                    }
                                                })
                                                .catch(() => {
                                                    // do nothing; errors should be handled by the
                                                    // beforeNextStep callback
                                                });
                                        } else {
                                            send({
                                                type: 'GO_TO_NEXT_STEP',
                                                value: {
                                                    nextStepId,
                                                },
                                            });
                                        }
                                    }
                                }}
                            >
                                <p className="px-4">
                                    {currentStep.nextStepLabel}
                                </p>
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
